import PropTypes from 'prop-types';
import React, { createContext, ReactNode, useContext } from 'react';
import useReducerWithSideEffects from 'use-reducer-with-side-effects';
import Cookies from 'js-cookie';
import { useI18n } from '@stitch-fix/i18n';
import { ProductDetailsPageAction } from './ProductDetailsPageAction';
import productDetailsPageContextReducer from './ProductDetailsPageReducer';
import { ProductDetailsPageState } from './ProductDetailsPageState';
import { ProductDetailsPageUnifiedState } from './ProductDetailsPageUnifiedState';

/**
 * This context houses state and actions used by the Product Details Page component and its descendants.
 *
 */

const ProductDetailsPageContext = createContext<
  [ProductDetailsPageUnifiedState, (action: ProductDetailsPageAction) => void]
  // eslint-disable-next-line @typescript-eslint/no-empty-function
>([{} as unknown as ProductDetailsPageUnifiedState, () => {}]);

export default ProductDetailsPageContext;

/**
 * A custom hook mostly to facilitate mocking/testing of this context.
 */
export const useProductDetailsPageContext = () =>
  useContext(ProductDetailsPageContext);

export const ProductDetailsPageConsumer = ProductDetailsPageContext.Consumer;

// eslint-disable-next-line no-restricted-syntax
function ProductDetailsPageProvider({
  children,
  initialProductSlug,
  initialExternalProductId,
  gated,
  urlScope,
  newEntryPoints,
  hideItemSuccessPath,
  shopAllSizes,
  canHideItem,
  canSaveItem,
  canScheduleAnchoredFix,
  shoppingBagEmpty,
  initialStateOverrides = {},
}: {
  children?: ReactNode;
  initialProductSlug?: string;
  initialExternalProductId?: string;
  gated?: boolean;
  urlScope: string;
  newEntryPoints?: boolean;
  hideItemSuccessPath?: string;
  shopAllSizes?: boolean;
  canHideItem?: boolean;
  canSaveItem?: boolean;
  canScheduleAnchoredFix?: boolean;
  shoppingBagEmpty?: boolean;
  initialStateOverrides?: object;
}) {
  const params = new URLSearchParams(window.location.search);

  const { locale } = useI18n();

  /**
   * The distinction between fallback and selected states is that
   * we store info in fallback states when the client was not actively
   * involved in actually selecting the product. Knowing this allows
   * us to implement different behavior for selected vs fallback (unselected)
   * states/products.
   */
  const initialState: ProductDetailsPageState = {
    ...{
      canHideItem,
      categoryId: params.get('category_id'),
      clientLoading: false,
      clientBusinessLine: null,
      clientHasPurchases: null,
      clientIsLoggedIn: false,
      clientFeatureFlags: {},
      visitorFeatureFlags: {},
      clientName: null,
      clientNeedsOnboarding: false,
      creditIncentivesEligibility: null,
      creditIncentivesEligibilityError: null,
      creditIncentivesEligibilityLoading: false,
      shoppingAcrossBusinessLinesEligibility: null,
      shoppingAcrossBusinessLinesEligibilityError: null,
      shoppingAcrossBusinessLinesEligibilityLoading: false,
      ctaTooltipDisplay: null,
      locale,
      isAuthenticatedClient: true,
      shopCategories: null,
      shopCategoriesLoading: false,
      shopCategoriesError: null,
      eligibleForCreditIncentives: false,
      eligibleForShoppingAcrossBusinessLines: false,
      productSkuId: params.get('sku-id') || params.get('sku_id') || '',
      productSlug: initialProductSlug,
      externalProductId: initialExternalProductId || null,
      shopProduct: null,
      selectedVariant: null,
      fallbackVariant: null,
      priceAndInventoryData: null,
      priceAndInventoryLoading: true,
      shopColorOptions: [],
      shopRecommendedColorOptionsBySize: [],
      shopRecommendedColorOptionsBySizeLoading: gated || false,
      shopColorOptionsBySize: [],
      shopPurchaseOptions: null,
      shopPurchaseOptionsLoading: true,
      shopSizeOptions: [],
      shopInseamOptions: [],
      selectedColorLabel: params.get('color'),
      fallbackColorLabel: null,
      selectedSizeLabel: params.get('size')?.replace(/-/g, ' ') || null,
      selectedSizeLabelOnPageLoad:
        params.get('size')?.replace(/-/g, ' ') || null,
      fallbackSizeLabel: null,
      oosData: {
        selectedSkuId: null,
        shopSizeOptions: [],
        tracked: false,
      },
      selectedUniqueColorId: null,
      selectedUniqueColorIdOnPageLoad: null,
      selectedUniqueColorIdHistory: [],
      selectedInseamLabel: params.get('inseam'),
      selectedInseamLabelOnPageLoad: params.get('inseam'),
      fallbackInseamLabel: null,
      selectedSkuId: null,
      fallbackSkuId: null,
      loading: true,
      source: params.get('source') || (gated ? '' : 'ungated_shop'),
      subSource: params.get('sub_source') || params.get('subsource') || '',
      pdpOutfitId: params.get('outfit_id'),
      showInventoryFailureModal: !!params.get('inventory_failure'),
      showRateLimitFailureModal: !!params.get('rate_limit_failure'),
      clientBusinessLineIsMismatched: false,
      showLargeImageUI: !!params.get('show_large_image_ui'),
      error: null,
      productBusinessLine: null,
      shoppingBagBusinessLine: null,
      shoppingBagBusinessLineIsMismatched: false,
      bagError: {},
      waysToWearIt: null,
      waysToWearItError: null,
      waysToWearItLoading: true,
      recommendedCollections: [],
      recommendedCollectionsError: null,
      recommendedCollectionsLoading: false,
      matchingSet: null,
      matchingSetError: null,
      matchingSetLoading: false,
      skipEventTracking: !!params.get('skip_event_tracking'),
      visitorId: Cookies.get('visitor_uuid'),
      selectedVariantURL: null,
      fallbackVariantURL: null,
      recommendedSizes: [],
      recommendedSizesLoading: gated || false,
      gated,
      recommendedSkuId:
        params.get('recommended-sku-id') || params.get('sku-id'),
      collectionName:
        params.get('collection') || params.get('category_id') || '',
      outfitId:
        (params.get('referring_sku_id') ? null : params.get('outfit_id')) || '',
      queryId: params.get('query_id'),
      referringSkuId: params.get('referring_sku_id'),
      hasNonPersonalizedFlag: params.get('non-personalized') === 'true',
      shopAllSizes,
      urlScope,
      clientSelectedColor: false,
      clientSelectedInseam: false,
      initialLoadSelectedState: !!params.get('sku-id'),
      newEntryPoints,
      hideItemSuccessPath: hideItemSuccessPath || '/wardrobe/bought-items',
      onRecommendedSizesTab: false,
      recommendedSkuSizeLabel: null,
      recommendedSkuInseamLabel: null,
      canSaveItem: canSaveItem || false,
      canScheduleAnchoredFix: canScheduleAnchoredFix || false,
      isStaticView: !!(
        params.get('inFixPreview') || params.get('styledFixShipment')
      ),
      showStyleProfileCta: false,
      productDescription: null,
      shoppingBagEmpty,
      previousUseShopProductQueryArguments: {
        productSkuId: null,
        externalProductId: null,
        gated: undefined,
      },
    },
    ...initialStateOverrides,
  };

  const [state, dispatch] = useReducerWithSideEffects(
    productDetailsPageContextReducer,
    initialState,
    null,
  );

  return (
    <ProductDetailsPageContext.Provider value={[{ ...state }, dispatch]}>
      {children}
    </ProductDetailsPageContext.Provider>
  );
}

ProductDetailsPageProvider.propTypes = {
  initialProductSlug: PropTypes.string,
  initialExternalProductId: PropTypes.string,
  children: PropTypes.element.isRequired,
  newEntryPoints: PropTypes.bool,
  hideItemSuccessPath: PropTypes.string,
  shopAllSizes: PropTypes.bool,
  shoppingBagEmpty: PropTypes.bool,
};

ProductDetailsPageProvider.defaults = {
  initialProductSlug: undefined,
  initialExternalProductId: undefined,
  newEntryPoints: false,
  hideItemSuccessPath: '/wardrobe/bought-items',
  shopAllSizes: false,
  shoppingBagEmpty: false,
};

export { ProductDetailsPageProvider };
